import { LucideIcon, Gift, Search, Globe, Megaphone, Palette, BarChart3, Mail, MapPin, Share2, Video, PenTool, Sparkles, Calendar, DollarSign } from "lucide-react";
import { services } from "./services";

export interface PopupConfig {
  icon: LucideIcon;
  headline: string;
  subheadline: string;
  benefits: string[];
  ctaText: string;
  funnelUrl: string; // New: redirect to funnel page
  offerType: string;
}

// Home page config - General offer
export const homePopupConfig: PopupConfig = {
  icon: Gift,
  headline: "Wait! Don't Miss This Special Offer",
  subheadline: "Get a free strategy consultation and discover how to grow your business online",
  benefits: [
    "30-minute strategy session with an expert",
    "Custom growth roadmap for your business",
    "No obligation—just actionable insights",
  ],
  ctaText: "Book My Free Consultation",
  funnelUrl: "/funnel/consultation",
  offerType: "home_consultation",
};

// SEO page config - Free audit
export const seoPopupConfig: PopupConfig = {
  icon: Search,
  headline: "Wait! Get Your Free SEO Audit",
  subheadline: "See exactly what's holding your website back from ranking #1 on Google",
  benefits: [
    "Complete technical SEO analysis",
    "Keyword opportunity report",
    "Competitor comparison insights",
  ],
  ctaText: "Get My Free SEO Audit",
  funnelUrl: "/funnel/seo-audit",
  offerType: "seo_audit",
};

// Web Design page config
export const webDesignPopupConfig: PopupConfig = {
  icon: Globe,
  headline: "Wait! Save 20% on Your New Website",
  subheadline: "Get a modern, high-converting website at an exclusive discount",
  benefits: [
    "Mobile-first responsive design",
    "SEO-optimized from day one",
    "Lightning-fast load speeds",
  ],
  ctaText: "Claim My 20% Discount",
  funnelUrl: "/funnel/web-design",
  offerType: "web_design_discount",
};

// PPC page config
export const ppcPopupConfig: PopupConfig = {
  icon: DollarSign,
  headline: "Stop Wasting Money on Bad Ads",
  subheadline: "Get a free Google Ads audit and find out where you're losing money",
  benefits: [
    "Identify wasted ad spend",
    "Discover missed opportunities",
    "Get actionable recommendations",
  ],
  ctaText: "Get My Free PPC Audit",
  funnelUrl: "/funnel/ppc",
  offerType: "ppc_audit",
};

// Function to generate service-specific config
export function getServicePopupConfig(serviceId: string): PopupConfig | null {
  const service = services.find((s) => s.id === serviceId);
  
  if (!service) return null;
  
  // Get first 3 benefits from the service
  const benefits = service.benefits.slice(0, 3);
  
  // Map specific services to their funnels
  const funnelMap: Record<string, string> = {
    "seo": "/funnel/seo-audit",
    "web-design": "/funnel/web-design",
    "ppc": "/funnel/ppc",
  };
  
  return {
    icon: service.icon,
    headline: `Wait! Don't Leave Empty-Handed`,
    subheadline: `Get expert help with ${service.title.toLowerCase()} and grow your business`,
    benefits,
    ctaText: funnelMap[serviceId] ? "Get Started Now" : "Book Free Consultation",
    funnelUrl: funnelMap[serviceId] || "/funnel/consultation",
    offerType: `service_${serviceId}`,
  };
}

// Main function to get popup config based on current route
export function getPopupConfigForRoute(pathname: string): PopupConfig {
  // SEO page - special audit offer
  if (pathname === "/services/seo") {
    return seoPopupConfig;
  }
  
  // Web design page
  if (pathname === "/services/web-design") {
    return webDesignPopupConfig;
  }
  
  // PPC page
  if (pathname === "/services/ppc") {
    return ppcPopupConfig;
  }
  
  // Other service pages - dynamic config
  const serviceMatch = pathname.match(/^\/services\/([^\/]+)$/);
  if (serviceMatch) {
    const serviceId = serviceMatch[1];
    const serviceConfig = getServicePopupConfig(serviceId);
    if (serviceConfig) {
      return serviceConfig;
    }
  }
  
  // Default to home config for all other pages
  return homePopupConfig;
}
